import { useMemo, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { Button } from '../../../../../../../components/button/button';
import { ExtendableBlock } from '../../../../../../../components/extendable-block/extendable-block';
import { IconButton } from '../../../../../../../components/icon-button/icon-button';
import { InlineInputs } from '../../../../../../../components/inline-inputs/inline-inputs';
import { Modal, ModalProps } from '../../../../../../../components/modal/modal';
import { defaultSocialIcon } from '../../../../../../../helpers/icon-maps';
import { useLang } from '../../../../../../../hooks/use-lang';
import { SocialType } from '../../../../../../../types/props';
import s from './branch-socials-modal.module.css';
import { availableMessengers, availableSocials } from './const';
import { Container } from '../../../../../../../components/container/container';
import { Input } from '../../../../../../../components/input/input';

interface Props {
  socials?: SocialType[];
  onChangeSocials: (data?: SocialType[]) => void;
}

export const BranchSocialsModal = (props: ModalProps & Props) => {
  const { socials, onChangeSocials, ...other } = props;
  const { t } = useLang();
  const [social, setSocial] = useState<SocialType[]>(() =>
    (socials ?? []).map((soc) => (soc.type ? soc : { ...soc, id: soc.id || crypto.randomUUID() })),
  );

  const otherSocials = useMemo(() => social.filter((soc) => !soc.type), [social]);

  const handleSave = () => {
    onChangeSocials(social);
    props.onClose();
  };

  const getSocialUrl = (type: string) => {
    return social.find((soc) => soc.type === type)?.url || '';
  };

  const handleChangeSocial = (type: string, url: string, id?: string) => {
    setSocial((prev) => {
      if (type) {
        const exists = prev.find((soc) => soc.type === type);
        if (exists) {
          return prev.map((soc) => (soc.type === type ? { ...soc, url } : soc));
        } else {
          return [...prev, { type, url }];
        }
      } else {
        return prev.map((soc) => (!soc.type && soc.id === id ? { ...soc, url } : soc));
      }
    });
  };

  const handleDeleteSocial = (id: string) => {
    setSocial((prev) => prev.filter((soc) => soc.id !== id));
  };

  const handleAddCustomSocial = () => {
    setSocial((prev) => [...prev, { type: '', url: '', id: crypto.randomUUID() }]);
  };

  return (
    <Modal {...other} className={s.feature} title={t('Добавить соц.сети и мессенджеры')}>
      <Container label={t('Социальные сети')}>
        <ul className={s.inputList}>
          {availableSocials.map((item) => (
            <InlineInputs key={item.id}>
              <IconButton variant="primary">{item.icon}</IconButton>
              <Input
                placeholder={item.id}
                value={getSocialUrl(item.id)}
                onChange={(e) => handleChangeSocial(item.id, e.currentTarget.value)}
              />
            </InlineInputs>
          ))}
        </ul>
      </Container>

      <Container label={t('Мессенджеры')}>
        <ul className={s.inputList}>
          {availableMessengers.map((item) => (
            <InlineInputs key={item.id}>
              <IconButton variant="primary">{item.icon}</IconButton>
              <Input
                placeholder={item.id}
                value={getSocialUrl(item.id)}
                onChange={(e) => handleChangeSocial(item.id, e.currentTarget.value)}
              />
            </InlineInputs>
          ))}
        </ul>
      </Container>

      <Container label={t('Другие')}>
        <ExtendableBlock
          button={{
            variant: 'primary',
            children: t('Добавить'),
            onClick: handleAddCustomSocial,
          }}
          className={s.expandable}
        >
          {otherSocials.map((item) => (
            <InlineInputs key={item.id} className={s.item}>
              <IconButton variant="primary">{defaultSocialIcon}</IconButton>
              <Input
                value={item.url}
                onChange={(e) => handleChangeSocial('', e.currentTarget.value, item.id)}
              />
              <IconButton variant="danger" onClick={() => handleDeleteSocial(item.id || '')}>
                <AiOutlineClose />
              </IconButton>
            </InlineInputs>
          ))}
        </ExtendableBlock>
      </Container>

      <div className={s.controls}>
        <Button onClick={props.onClose} variant="secondary">
          {t('Отменить')}
        </Button>
        <Button onClick={handleSave}>{t('Сохранить')}</Button>
      </div>
    </Modal>
  );
};
